import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleGenerativeAI } from "@google/generative-ai";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const modelName = "gemini-2.0-flash-lite-001";

const systemPrompt = `You are Ask Krishna, a knowledgeable guide on Indian mythology and traditional toys. 
Always reply with 2–4 specific suggestions or answers right away.
Guidelines:
- Start with helpful responses, no lengthy introductions.
- Focus on Indian mythology stories, characters, teachings, and traditional toys like Bal Ganesha.
- Keep replies under 6 short lines, using simple bullets (no stars or long text).
- Stay on topics related to Indian mythology, gods, goddesses, epics, and traditional toys only.
- Do not provide any inappropriate, harmful, or offensive content.
- Do not discuss topics outside of Indian mythology and traditional toys.
- Do not provide personal advice or medical recommendations.`;

export default function FloatingChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const endRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const navigate = useNavigate();
  const lastRequestTime = useRef(0);
  const requestCount = useRef(0);

  // Check if user is authenticated
  const getTokenFromLocalStorage = localStorage.getItem("customer")
    ? JSON.parse(localStorage.getItem("customer"))
    : null;

  const isAuthenticated = () => {
    if (!getTokenFromLocalStorage || !getTokenFromLocalStorage.token) {
      return false;
    }
    return true;
  };

  // Handle chat open/close with authentication check
  const handleChatToggle = () => {
    if (!open) {
      // Check authentication when opening the chat
      if (!isAuthenticated()) {
        toast.error("You need to login first to ask Krishna");
        return;
      }
    }
    setOpen((v) => !v);
  };

  const genAI = useMemo(() => {
    const key = process.env.REACT_APP_GEMINI_API_KEY || "";
    return key ? new GoogleGenerativeAI(key) : null;
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 0);
  }, [open, messages.length]);

  // Initialize speech recognition
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window)) {
      console.warn('Speech recognition not supported');
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error', event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.stop();
    };
  }, []);

  // Clean response text by removing markdown-style formatting
  const cleanResponseText = (text) => {
    if (!text) return text;
    
    // Remove leading asterisks and spaces used for markdown bullet points
    return text
      .split('\n')
      .map(line => line.trimStart().replace(/^\*+\s*/, ''))
      .filter(line => line.length > 0)
      .join('\n');
  };

  // Text-to-speech function using ElevenLabs API
  const speakText = async (text) => {
    if (!text) return;
    
    setIsSpeaking(true);
    
    try {
      const ELEVENLABS_API_KEY = process.env.REACT_APP_ELEVENLABS_API_KEY;
      if (!ELEVENLABS_API_KEY) {
        // Fallback to Web Speech API if ElevenLabs key is not available
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
        return;
      }

      // Use ElevenLabs API for higher quality speech
      const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${process.env.REACT_APP_ELEVENLABS_VOICE_ID || '21m00Tcm4TlvDq8ikWAM'}`, {
        method: 'POST',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': ELEVENLABS_API_KEY
        },
        body: JSON.stringify({
          text: text,
          model_id: 'eleven_monolingual_v1',
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.5
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      audioRef.current = new Audio(audioUrl);
      audioRef.current.onended = () => setIsSpeaking(false);
      audioRef.current.onerror = () => setIsSpeaking(false);
      audioRef.current.play();
    } catch (error) {
      console.error('Text-to-speech error:', error);
      // Fallback to Web Speech API
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
    }
  };

  // Handle voice input
  const handleVoiceInput = () => {
    if (!isAuthenticated()) {
      toast.error("You need to login first to ask Krishna");
      return;
    }
    
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      recognitionRef.current?.start();
      setIsListening(true);
    }
  };

  // Handle voice output for the last assistant message
  const handleVoiceOutput = () => {
    if (!isAuthenticated()) {
      toast.error("You need to login first to ask Krishna");
      return;
    }
    
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.role === 'model') {
      speakText(lastMessage.content);
    }
  };

  // Rate limiting function
  const checkRateLimit = () => {
    const now = Date.now();
    const timeWindow = 60000; // 1 minute window
    
    // Reset count if outside time window
    if (now - lastRequestTime.current > timeWindow) {
      requestCount.current = 0;
      lastRequestTime.current = now;
    }
    
    // Check if we've exceeded the limit (10 requests per minute)
    if (requestCount.current >= 10) {
      return false;
    }
    
    requestCount.current++;
    lastRequestTime.current = now;
    return true;
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim()) return;
    
    // Check rate limit
    if (!checkRateLimit()) {
      setMessages((m) => m.concat({ 
        role: "model", 
        content: "You've made too many requests. Please wait a moment before asking another question." 
      }));
      toast.warn("Too many requests. Please wait before sending another message.");
      return;
    }
    
    if (!genAI) {
      setMessages((m) => m.concat({ role: "model", content: "Missing API key. Set REACT_APP_GEMINI_API_KEY and restart." }));
      return;
    }
    
    const userMsg = { role: "user", content: input.trim() };
    setMessages((m) => m.concat(userMsg));
    setInput("");
    setLoading(true);
    
    try {
      const model = genAI.getGenerativeModel({ 
        model: "gemini-2.0-flash-lite-001", 
        systemInstruction: systemPrompt,
        safetySettings: [
          {
            category: "HARM_CATEGORY_HARASSMENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE",
          },
          {
            category: "HARM_CATEGORY_HATE_SPEECH",
            threshold: "BLOCK_MEDIUM_AND_ABOVE",
          },
          {
            category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE",
          },
          {
            category: "HARM_CATEGORY_DANGEROUS_CONTENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE",
          },
        ],
      });
      
      // Prepare history - only include messages that come after the first user message
      // Find the first user message index
      const firstUserIndex = messages.findIndex(m => m.role === "user");
      
      // If there's a user message, include the conversation from that point onward
      let history = [];
      if (firstUserIndex !== -1) {
        // Include messages from the first user message onwards
        const conversationHistory = messages.slice(firstUserIndex);
        history = conversationHistory.map(m => ({
          role: m.role,
          parts: [{ text: m.content }]
        }));
      }
      
      const chat = model.startChat({ history });
      const result = await chat.sendMessage(userMsg.content);
      const text = await result.response.text();
      
      // Clean the response text to remove asterisks and markdown formatting
      const cleanedText = cleanResponseText(text);
      
      const assistantMsg = { role: "model", content: cleanedText };
      setMessages((m) => m.concat(assistantMsg));
      
      // Auto speak the response if user has used voice input
      if (isListening || isSpeaking) {
        speakText(cleanedText);
      }
    } catch (err) {
      console.error('API Error:', err);
      
      // Handle rate limiting error specifically
      if (err.message && err.message.includes('429')) {
        setMessages((m) => m.concat({ 
          role: "model", 
          content: "I'm receiving too many requests right now. Please wait a moment before asking another question." 
        }));
        toast.warn("Too many requests. Please wait before sending another message.");
      } else {
        setMessages((m) => m.concat({ role: "model", content: `Error: ${err.message || err}` }));
      }
    } finally {
      setLoading(false);
      setIsListening(false);
    }
  };

  // Stop speech when component unmounts or chat closes
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  return (
    <>
      {/* Floating button - always visible */}
      <button
        onClick={handleChatToggle}
        aria-label="Open chat"
        style={{
          position: "fixed",
          right: 16,
          bottom: 16,
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "#e11d48",
          color: "#fff",
          border: "none",
          boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
          cursor: "pointer",
          zIndex: 1000,
        }}
      >
        💬
      </button>

      {/* Panel - only shown when open and user is authenticated */}
      {open && isAuthenticated() && (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 84,
            width: 360,
            maxWidth: "calc(100% - 32px)",
            height: 480,
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 12px 28px rgba(0,0,0,0.2)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            zIndex: 1000,
          }}
        >
          <div style={{ padding: 12, borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 700 }}>Ask Krishna</div>
            <div>
              <button 
                onClick={handleVoiceOutput}
                disabled={isSpeaking}
                style={{ 
                  border: "none", 
                  background: "transparent", 
                  cursor: "pointer",
                  marginRight: 8,
                  color: isSpeaking ? "#666" : "#e11d48"
                }}
                aria-label="Listen to last message"
              >
                {isSpeaking ? "🔊" : "🔈"}
              </button>
              <button 
                onClick={() => setOpen(false)} 
                style={{ border: "none", background: "transparent", cursor: "pointer" }}
              >
                ×
              </button>
            </div>
          </div>
          <div style={{ flex: 1, padding: 12, overflow: "auto", background: "#fafafa" }}>
            {/* Welcome message - always shown as the first message */}
            {messages.length === 0 && (
              <div style={{ margin: "8px 0", display: "flex", justifyContent: "flex-start" }}>
                <div style={{
                  background: "#fff",
                  color: "#111",
                  padding: "8px 12px",
                  borderRadius: 12,
                  maxWidth: "80%",
                  border: "1px solid #eee",
                }}>
                  Hi! I'm Ask Krishna - your guide to Indian mythology and traditional toys. Ask me about gods, goddesses, epic stories, or our Bal Ganesha collection!
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ margin: "8px 0", display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                <div style={{
                  background: m.role === "user" ? "#e11d48" : "#fff",
                  color: m.role === "user" ? "#fff" : "#111",
                  padding: "8px 12px",
                  borderRadius: 12,
                  maxWidth: "80%",
                  border: m.role === "user" ? "none" : "1px solid #eee",
                }}>
                  {m.content}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} style={{ display: "flex", gap: 8, padding: 12, borderTop: "1px solid #eee", background: "#fff" }}>
            <button
              type="button"
              onClick={handleVoiceInput}
              style={{ 
                background: isListening ? "#e11d48" : "transparent", 
                color: isListening ? "#fff" : "#e11d48",
                border: "1px solid #e11d48",
                borderRadius: 8,
                width: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer"
              }}
              aria-label={isListening ? "Stop listening" : "Start voice input"}
            >
              {isListening ? "●" : "🎤"}
            </button>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about Krishna, Ram, Shiva, Durga, or Bal Ganesha toys.."
              style={{ flex: 1, padding: 10, border: "1px solid #ddd", borderRadius: 8 }}
            />
            <button type="submit" disabled={loading} style={{ background: "#e11d48", color: "#fff", border: "none", padding: "0 14px", borderRadius: 8 }}>
              {loading ? "..." : "➤"}
            </button>
          </form>
        </div>
      )}
    </>
  );
}